/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import styles from './styles';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { enqueueSnackbar } from '../../../../store/actions/snackbar';
import { Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { CancelPresentationRounded, DeleteRounded, PersonAddAlt, SaveRounded } from '@mui/icons-material';
import SelectEditInputTextField from '../../SelectEditInputTextField';
import ModalDialog from '../../ModalDialog';
import Datagrid from '../../Datagrid';
import columns from './table_config';
import PropTypes from 'prop-types';
import DeleteConfirmationModal from '../../../deleteConfirmationModal';
import useUnsavedChanges from '../../../../hooks/useUnsavedChanges';
import useUnitConversion from '../../../../hooks/useUnitConversion';
import { UNIT_SYSTEM } from '../../../../shared/config/hydraulics_constants';
import { addNewObservationRow, createObservation, deleteObservationRow, listObservation, updateObservation } from '../../../../store/actions/observation';
import { APP_ACTION_TYPES, MAIN_DEPTH_POINTS, MAX_OBSERVATION_POINT_NAME_LENGTH, MAX_ROW_LIMITS, PERMISSION_DIALOG_SOURCES, TOOLTIPS, WELLS_OBSERVATION_POINTS } from '../../../../utils/constants';
import { COLORS } from '../../../../shared/config/colors';
import SelectNameAutoComplete from '../SelectNameAutoComplete';
import PermissionsModal from '../../../permissions';
import { checkHydraulicsAdmin, checkPermissions } from '../../../../utils/checkPermissions';
import CreatedByInfoDialog from '../../CreatedByInfoDialog';
import { REDUX_ACTIONS } from '../../../../shared/config/constants';

const WellsObservationPointsModal = ({
  onCloseClick,
  isOpen,
  data,
  anchorPoint,
  runDetails,
  showWellName
}) => {
  const classes = styles();
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const [properties, setProperties] = useState([]);
  const [invalidRows, setInvalidRows] = useState({});
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteRow, setDeleteRow] = useState();
  const { unit, convertUnitSystem } = useUnitConversion();
  const observation = useSelector((state) => state.observation);
  const {
    unSavedExists,
    handleClose,
    UnsavedChangesModal
  } = useUnsavedChanges(properties, onCloseClick);
  const [namesSelection, setNamesSelection] = useState(WELLS_OBSERVATION_POINTS);
  const disableAction = observation?.loading || properties.some(item => item?.isDirty === true || item?.isNewRow);
  const user = useSelector((state) => state.currentUser);
  const [permissions, setPermissions] = useState({
    open: false,
    data: {}
  });
  const isHydraulicsAdmin = checkHydraulicsAdmin(
    user?.type,
    user?.accessLevels
  );

  useEffect(() => {
    if (!observation?.data?.length || !anchorPoint) {
      dispatch(listObservation(data?.wellsInfoId, onCloseClick));
    }
    return () => {
      dispatch({
        type: REDUX_ACTIONS.OBSERVATION_LIST_RESET
      });
    };
  }, [isOpen]);

  const getUniqueNames = (arr = []) => {
    return [...new Set(arr.map(item => item?.name).filter(Boolean))];
  };

  useEffect(() => {
    if (observation?.data?.length && !observation.loading) {
      setProperties(observation.data);
      setNamesSelection([...WELLS_OBSERVATION_POINTS, ...getUniqueNames(observation?.data)]);
    } else {
      setProperties([]);
    }
  }, [observation?.data, observation.loading]);

  const handleAddObservation = () => {
    dispatch(addNewObservationRow());
  };

  useEffect(() => {
    if (properties[0]?.name === '') {
      const id = properties[0]._id;
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: 0
        });
        apiRef.current.setCellMode(id, 'name', 'edit');
        setTimeout(() => {
          apiRef.current.setCellFocus(id, 'name');
        }, 50);
      }, 50);
      apiRef.current.setPage(0);
    }
  }, [properties?.length]);

  const hasDuplicateDepthAndName = (arr, row) => {
    const seenMd = new Set();
    const seenNames = new Set();
    const seenCombinations = new Set();

    for (const item of arr) {
      const md = Number(item.md);
      const name = item.name?.toLowerCase()?.trim();
      const comboKey = `${md}-${name}`;

      if (seenCombinations.has(comboKey)) {
        dispatch(
          enqueueSnackbar(
            'Name and Measured Depth combination already exists',
            'error',
            new Date().getTime() + Math.random()
          )
        );
        setInvalidRows(prev => ({ ...prev, [row._id]: true }));
        return true;
      }

      if (seenMd.has(md)) {
        dispatch(
          enqueueSnackbar(
            'Measured Depth already exists',
            'error',
            new Date().getTime() + Math.random()
          )
        );
        setInvalidRows(prev => ({ ...prev, [row._id]: true }));
        return true;
      }

      if (seenNames.has(name)) {
        dispatch(
          enqueueSnackbar(
            'Name already exists',
            'error',
            new Date().getTime() + Math.random()
          )
        );
        setInvalidRows(prev => ({ ...prev, [row._id]: true }));
        return true;
      }

      seenMd.add(md);
      seenNames.add(name);
      seenCombinations.add(comboKey);
    }

    return false;
  };

  const checkObservationValue = (value) => {
    const row = value;
    const invalidFields = [];
    if (!row?.name) invalidFields.push('Name');
    if (row?.md === undefined || row?.md === null || row?.md === '') invalidFields.push('Measured depth');
    if (invalidFields.length === 0) {
      setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      return true;
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
          `Please enter the following details: ${invalidFields.join(', ')}`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const validateProperties = (row) => {
    const { name } = row;
    if (typeof name !== 'string' || name.length > MAX_OBSERVATION_POINT_NAME_LENGTH) {
      dispatch(
        enqueueSnackbar(
          `Name should be limited to ${MAX_OBSERVATION_POINT_NAME_LENGTH} characters`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return true;
    }
    if (Number(row?.md) < 0) {
      dispatch(
        enqueueSnackbar(
          `Measured Depth should be between 0.0 and ${runDetails?.hole_depth}`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return true;
    }
    return false;
  };

  const handleSave = (row) => {
    if (checkObservationValue(row)) {
      if (!hasDuplicateDepthAndName(properties, row) && !validateProperties(row)) {
        const convertedMd = unit !== UNIT_SYSTEM.US
          ? convertUnitSystem(row?.md, observation?.units?.md.conversionFactor)
          : Number(row?.md);
        const isReservedName = MAIN_DEPTH_POINTS.some(
          point => point.toLowerCase() === row?.name?.toLowerCase()
        );

        const toTitleCase = (text) => text.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

        const body = {
          name: row?.name,
          md: convertedMd
        };

        if (isReservedName) {
          body.name = toTitleCase(row?.name);
        }
        setInvalidRows(prev => ({ ...prev, [row._id]: false }));
        if (row?.isNewRow) {
          dispatch(createObservation(body, data?.wellsInfoId));
        } else {
          dispatch(updateObservation(body, data?.wellsInfoId, row?._id));
        }
      }
    }
  };

  const removeObservation = (row) => {
    if (row?.isNewRow) {
      const updatedObservation = properties.filter(item => item._id !== row?._id);
      setProperties(updatedObservation);
      dispatch(listObservation(data?.wellsInfoId));
    } else {
      if (row?.name === anchorPoint) {
        dispatch(
          enqueueSnackbar(
            'Deletion failed: The selected observation is currently set as the anchor point and cannot be removed.',
            'error',
            new Date().getTime() + Math.random()
          )
        );
      } else {
        dispatch(deleteObservationRow(row?._id, data?.wellsInfoId));
      }
    }
    setDeleteAction(false);
    setDeleteRow({});
  };

  const closeHandler = () => {
    if (unSavedExists) {
      handleClose(false);
      return;
    }
    onCloseClick();
  };

  const getModalTitle = () => {
    const wellNameText = showWellName && data?.name;
    const displayText = showWellName ? (wellNameText?.length > 20 ? wellNameText.slice(0, 20) + '... |' : `${wellNameText} |`) : '';
    return (
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item xs zeroMinWidth>
          <Typography
            noWrap
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: '20px',
              fontWeight: 'bold'
            }}
          >
            {displayText} Well&apos;s Observation Points <CreatedByInfoDialog data={observation?.createdDetails} loading={disableAction} />
          </Typography>
        </Grid>
        <Grid item>
          {isHydraulicsAdmin
            ? (
              <Tooltip title={TOOLTIPS.viewPermissions}>
                <span>
                  <IconButton
                    variant="solid"
                    key="permissions"
                    disabled={disableAction || properties?.length === 0}
                    sx={{ color: observation?.permissionId && 'green' }}
                    onClick={() => {
                      setPermissions({
                        open: true,
                        data: {
                          ...data,
                          _id: observation?._id,
                          permissionId: observation?.permissionId
                        }
                      });
                    }}
                  >
                    <PersonAddAlt fontSize='large' />
                  </IconButton>
                </span>
              </Tooltip>
              )
            : null}
          <IconButton
            onClick={closeHandler}
          >
            <CancelPresentationRounded fontSize='large' />
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  const updateData = (id, field, value) => {
    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === id) {
          const isDirty = value !== item[field];
          return {
            ...item,
            [field]: typeof value === 'string' ? value : Number(value),
            isDirty
          };
        }
        return item;
      });
      return updatedProperties;
    });
  };

  const renderValueCell = (params) => {
    return <SelectEditInputTextField numberFormatOnly {...params} updateData={updateData} />;
  };

  const renderActionCell = ({ row }) => {
    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          disabled={!row.isDirty || observation.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.UPDATE, observation?.permissions))}
          onClick={() => {
            setTimeout(() => {
              handleSave(row);
            }, 100);
          }}
        >
          <SaveRounded />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={observation.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.DELETE, observation?.permissions))}
          onClick={() => {
            setDeleteAction(true);
            setDeleteRow(row);
          }}
        >
          <DeleteRounded />
        </IconButton>
      </Grid>
    );
  };

  const renderNameEditCell = (params) => {
    return <SelectNameAutoComplete closeCellAfterSelect={true} {...params} data={namesSelection} updateData={updateData} maxLength={MAX_OBSERVATION_POINT_NAME_LENGTH} />;
  };

  return (
    <ModalDialog
      title={getModalTitle()}
      isOpen={isOpen}
      onClose={closeHandler}
      dialogClass={clsx(classes.dialogModal, showWellName ? classes.dialogMaxWidth : classes.dialogMinWidth)}
    >
      <Grid container maxWidth='780px'>
        <Grid container sx={{ display: !checkPermissions(APP_ACTION_TYPES.CREATE, observation?.permissions) && 'none !important' }}>
          <Grid item marginLeft='12px' className={classes.customTool}>
            <Typography variant='button' fontWeight='700' onClick={handleAddObservation} className={properties?.length === MAX_ROW_LIMITS.WELLS_OBSERVATION || properties.some(item => item.isDirty === true) || observation?.loading ? classes.disableIcon : ''}>+ Add</Typography>
          </Grid>
          <Grid item marginLeft='12px' className={classes.entryLabel}>
            <Typography variant='h6' color={COLORS.LIGHT_GREY} fontWeight='500'>{`(Note: Maximum ${MAX_ROW_LIMITS.WELLS_OBSERVATION} entries are allowed)`}</Typography>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Datagrid
            apiRef={apiRef}
            data={properties}
            sx={{
              marginLeft: '-6px',
              '.MuiDataGrid-cell': {
                fontSize: '1rem',
                fontWeight: 900
              },
              '.MuiDataGrid-columnHeader': {
                fontSize: '1.2rem',
                fontWeight: '900 !important'
              },
              '& .MuiDataGrid-footerContainer': {
                display: 'none',
                height: '0px'
              },
            }}
            loading={observation?.loading}
            columns={columns(renderNameEditCell, renderValueCell, renderActionCell, observation?.units, unit)}
            onRowClick={(params, evt) => {
              evt.preventDefault();
              evt.stopPropagation();
            }}
            disableStickyHeader={false}
            height={'30vh'}
            autoHeight={false}
            getRowId={(row) => row._id}
            getRowHeight={() => 'auto'}
            hideFooterPagination
            hideFooterSelectedRowCount
            getRowClassName={(params) => {
              return invalidRows[params.id] ? classes.invalidRow : '';
            }}
            pageSize={100}
            editMode="cell"
            toolbar={{
              options: {
                columns: false,
                filters: false,
                density: false,
                export: false
              },
            }}
            enableDirtyLock
            permissions={observation?.permissions}
          />
        </Grid>
      </Grid>
        {
            deleteAction && <DeleteConfirmationModal
                open={deleteAction}
                handleDelete={removeObservation}
                handleClose={setDeleteAction}
                deleteRow={deleteRow}
            />
        }
      <UnsavedChangesModal />
      {permissions?.open
        ? (
          <PermissionsModal
            isOpen={permissions?.open}
            data={
              {
                ...permissions?.data,
                collection: PERMISSION_DIALOG_SOURCES?.wellsObservationPoints,
              }
            }
            onCloseClick={() => {
              setPermissions({
                open: false,
                data: {}
              });
            }}
            refreshParentDialog={() => {
              dispatch(listObservation(data?.wellsInfoId));
            }}
          />
          )
        : null}
    </ModalDialog>
  );
};
WellsObservationPointsModal.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  data: PropTypes.any,
  anchorPoint: PropTypes.any,
  runDetails: PropTypes.any,
  showWellName: PropTypes.bool
};

export default WellsObservationPointsModal;
